import { useCallback, useState } from "react";
import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import { useMountRead, type MountReadStatus } from "@/hooks/useMountRead";
import type { ProductCardData } from "@models/product";

// The most product ids GET /products/related takes (GetRelatedProductsBatchValidator).
// More is a 400, which would fail the rail for a very large cart.
export const MAX_RELATED_SOURCE_IDS = 50;

export interface RelatedRail {
    products: ProductCardData[];
    status: MountReadStatus;
    retry: () => Promise<boolean>;
}

// The cart's Related Items rail in ONE read (#449, audit B-05), however many
// products the cart holds. `productIdsKey` is the cart's distinct product ids,
// sorted and comma-joined, so a quantity edit (same products) issues no read.
// The API scores, sums, orders and caps the rail; its rows are shown as given.
//
// The read is the page's own (useMountRead, #407). It is re-issued when the
// product set changes and when the destination does (a new
// getRelatedProductsBatch), and only the newest read may write its answer.
// Rows are shown only once the current read has loaded. A rail held from the
// previous cart or destination answered something else (a removed product's
// relations, products restricted at the new destination), so it is never
// shown under a read in flight or one that failed.
export const useRelatedRail = (productIdsKey: string): RelatedRail => {
    const { getRelatedProductsBatch } = useProductApi();
    const [rows, setRows] = useState<ProductCardData[]>([]);

    const read = useCallback(
        async (isCurrent: () => boolean) => {
            // An empty cart has nothing to relate to: no request.
            if (!productIdsKey) {
                if (isCurrent()) setRows([]);
                return;
            }
            const productIds = productIdsKey.split(",");
            const answer = await getRelatedProductsBatch(
                productIds.slice(0, MAX_RELATED_SOURCE_IDS),
            );
            if (isCurrent()) {
                // The API excludes only the ids it was sent. Past the cap, a
                // cart product it was not sent may come back as "related".
                const cartProductIds = new Set(productIds);
                setRows(answer.filter((p) => !cartProductIds.has(p.id)));
            }
        },
        [productIdsKey, getRelatedProductsBatch],
    );

    const { readStatus, reload } = useMountRead(read);

    return {
        products: readStatus === "loaded" ? rows : [],
        status: readStatus,
        retry: reload,
    };
};
